import _ from 'lodash';
import { useState, useEffect, FormEvent, MouseEvent } from 'react';
import { Form, FormGroup, FormControl, FormLabel, Button, Alert } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import toastr from '../toastr';
import BlazeForm from '../form/Form';
import LoginPanel from './LoginPanel';
import AuthService from '../../services/AuthService';
import history from '../../history';
import MicrosoftLoginButton from '../user/MicrosoftLoginButton';
import GoogleLoginButton from '../user/GoogleLoginButton';
import { useLocation } from 'react-router-dom';

interface LocationState {
  from: {
    pathname: string;
  };
}

const LoginForm = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [warning, setWarning] = useState('');
  const config = useSelector((state: any) => state.app.config);
  const location = useLocation<LocationState>();

  const dispatch = useDispatch();

  const handleLogout = (event: MouseEvent) => {
    event.preventDefault();
    AuthService.logout();
    history.push('/login');
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const error = urlParams.get('error');

    if (error) {
      setWarning(error);
    }
  }, []);

  const login = (username: string, password: string, mfaToken?: string) => {
    if (!username || !password) {
      toastr.error('Error', 'Enter valid username and password');
      return;
    }

    AuthService.login({ username, password, mfaToken })
      .then(({ mfaEnabled, user, branch, codes }) => {
        if (mfaEnabled) {
          setMfaEnabled(mfaEnabled);
          return;
        }

        // TODO Probably could have one dispatch
        dispatch({ type: 'SET_USER', payload: user });
        dispatch({ type: 'SET_BRANCH', payload: branch });
        dispatch({ type: 'SET_CODES', payload: codes });

        if (user.id) {
          const q = new URLSearchParams(window.location.search);
          if (q.get('from')) {
            history.push(q.get('from') || '');
          } else if (_.has(location, 'state.from.pathname')) {
            history.push(location.state.from.pathname);
          } else {
            history.push('/');
          }
        }
      })
      .catch(err => {
        toastr.error(err.message);
      });
  };

  const handleLogin = (event: FormEvent) => {
    event.preventDefault();
    setWarning('');
    login(username, password);
  };

  const handleSubmitToken = (mfaToken: string) => {
    login(username, password, mfaToken);
  };

  const renderAlerts = () => {
    if (warning) {
      return (
        <Alert variant="danger" onClose={() => setWarning('')}>
          {warning}
        </Alert>
      );
    }

    return null;
  };

  const renderLoggedInMessage = () => {
    return (
      <>
        <Button variant="secondary" block onClick={() => history.push('/home')}>
          Home
        </Button>
        <Button variant="primary" block onClick={handleLogout} className="logout-btn">
          Logout
        </Button>
      </>
    );
  };

  const renderLoginForm = () => {
    if (AuthService.loggedIn()) {
      return renderLoggedInMessage();
    }

    return (
      <Form onSubmit={handleLogin}>
        <FormGroup>
          <FormLabel>Username</FormLabel>
          <FormControl
            autoFocus
            type="text"
            id="login-username"
            value={username}
            onChange={event => setUsername(event.target.value)}
          />
          <FormControl.Feedback />

          <FormLabel>Password</FormLabel>
          <FormControl
            type="password"
            id="login-password"
            value={password}
            onChange={event => setPassword(event.target.value)}
          />
          <FormControl.Feedback />
        </FormGroup>
        <div className="login-controls">
          <Button variant="primary" type="submit" block onClick={handleLogin} id="login-btn">
            Login
          </Button>
          {!config.features.disablePasswordReset && (
            <Button variant="secondary" block onClick={() => history.push('forgotpassword')} id="forgot-pw-btn">
              Forgot Password
            </Button>
          )}
        </div>
      </Form>
    );
  };

  const renderMfaForm = () => {
    const layout = [
      {
        name: 'token',
        label: 'Token',
      },
    ];

    return (
      <BlazeForm
        layout={layout}
        exportFormat="object"
        submit={(data: any) => {
          handleSubmitToken(data.token);
        }}
      />
    );
  };

  const renderForm = () => {
    if (username && password && mfaEnabled) {
      return renderMfaForm();
    }

    return renderLoginForm();
  };

  return (
    <LoginPanel>
      {renderAlerts()}
      {renderForm()}
      {config.oauth.providers.azure.enabled && (
        <>
          <hr />
          <MicrosoftLoginButton />
        </>
      )}
      {config.oauth.providers.google.enabled && (
        <>
          <hr />
          <GoogleLoginButton />
        </>
      )}
    </LoginPanel>
  );
};

export default LoginForm;
